import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiLinkedin, FiTwitter, FiMail, FiArrowRight, FiAward, FiBookOpen, FiUsers, FiShield } from 'react-icons/fi';
import { HiOutlineScale } from 'react-icons/hi';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

function Team() {
  const founder = {
    name: 'Adv. Ravi Singh',
    role: 'Founder & CEO',
    experience: '20+ Years of Experience',
    bio: 'Advocate Ravi Singh is a distinguished legal professional with over 20 years of exemplary practice in Criminal Law, Property Disputes, Civil Litigation, and Constitutional matters. A graduate from a prestigious law university, he has successfully handled thousands of cases across District Courts, High Courts, and the Supreme Court of India. His deep understanding of Indian jurisprudence and unwavering commitment to justice has earned him recognition as one of Bihar\'s most trusted legal minds.',
    specializations: ['Criminal Law & IPC', 'Property Disputes', 'Civil Litigation', 'Constitutional Law', 'Family Law', 'Land Revenue Cases'],
    stats: [
      { label: 'Years Experience', value: '20+' },
      { label: 'Cases Won', value: '5000+' },
      { label: 'Happy Clients', value: '8000+' },
      { label: 'Courts Covered', value: '15+' },
    ],
    image: 'https://ui-avatars.com/api/?name=Ravi+Singh&size=256&background=1e3a5f&color=d4af37&bold=true&font-size=0.4',
  };

  const teamMembers = [
    {
      id: 1,
      name: 'Adv. Priya Kumari',
      role: 'Senior Associate - Criminal Law',
      experience: '12 Years Experience',
      bio: 'Expert in criminal defense and IPC cases. Specializes in bail matters, criminal appeals, and trial advocacy across Bihar courts.',
      specializations: ['Criminal Defense', 'Bail Matters', 'Trial Advocacy'],
      image: 'https://ui-avatars.com/api/?name=Priya+Kumari&size=256&background=2a5a8f&color=fff&bold=true&font-size=0.4',
    },
    {
      id: 2,
      name: 'Adv. Amit Kumar Sinha',
      role: 'Senior Associate - Property & Civil',
      experience: '15 Years Experience',
      bio: 'Seasoned practitioner in property disputes, land acquisition cases, and civil litigation. Known for meticulous case preparation and strong courtroom presence.',
      specializations: ['Property Disputes', 'Land Acquisition', 'Civil Litigation'],
      image: 'https://ui-avatars.com/api/?name=Amit+Sinha&size=256&background=1e3a5f&color=d4af37&bold=true&font-size=0.4',
    },
    {
      id: 3,
      name: 'Adv. Neha Sharma',
      role: 'Associate - Family & Constitutional Law',
      experience: '8 Years Experience',
      bio: 'Dedicated to family law matters including divorce, custody, and inheritance cases. Also handles constitutional petitions and PIL matters.',
      specializations: ['Family Law', 'Divorce & Custody', 'Constitutional Petitions'],
      image: 'https://ui-avatars.com/api/?name=Neha+Sharma&size=256&background=2a5a8f&color=fff&bold=true&font-size=0.4',
    },
    {
      id: 4,
      name: 'Adv. Rajesh Verma',
      role: 'Associate - Corporate & Tax Law',
      experience: '10 Years Experience',
      bio: 'Specializes in corporate governance, taxation matters, and business compliance. Advises startups and established businesses across Bihar.',
      specializations: ['Corporate Law', 'Taxation', 'Business Compliance'],
      image: 'https://ui-avatars.com/api/?name=Rajesh+Verma&size=256&background=1e3a5f&color=d4af37&bold=true&font-size=0.4',
    },
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f1f35 0%, #1e3a5f 50%, #172e4d 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute bottom-10 right-20 w-80 h-80 rounded-full" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.3) 0%, transparent 70%)' }} />
          <div className="absolute top-10 left-20 w-60 h-60 rounded-full" style={{ background: 'radial-gradient(circle, rgba(20,184,166,0.3) 0%, transparent 70%)' }} />
        </div>
        <div className="max-w-6xl mx-auto px-4 py-16 md:py-20 text-center relative">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm text-white/80 mb-6">
            <HiOutlineScale className="w-4 h-4 text-accent-400" />
            Bihar's Trusted Legal Team
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl md:text-5xl font-bold text-white mb-4">Meet Our Legal Experts</motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-white/50 text-lg max-w-xl mx-auto">A dedicated team of experienced advocates committed to delivering justice and protecting your rights across Bihar and beyond</motion.p>
        </div>
      </div>

      {/* Founder Section */}
      <div className="max-w-6xl mx-auto px-4 py-14">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-3xl border border-secondary-100 shadow-card overflow-hidden mb-16">
          <div className="grid lg:grid-cols-5 gap-0">
            {/* Founder Image/Avatar */}
            <div className="lg:col-span-2 bg-gradient-to-br from-primary-500 to-primary-800 flex items-center justify-center p-12">
              <div className="text-center">
                <div className="w-36 h-36 rounded-3xl overflow-hidden border-2 border-white/20 mx-auto mb-6 shadow-xl">
                  <img src={founder.image} alt={founder.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-1">{founder.name}</h3>
                <p className="text-accent-400 font-semibold mb-1">{founder.role}</p>
                <p className="text-white/50 text-sm">{founder.experience}</p>
                <div className="flex items-center justify-center gap-3 mt-5">
                  <a href="#" className="w-9 h-9 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center text-white/60 hover:bg-white/20 hover:text-white transition-colors"><FiLinkedin className="w-4 h-4" /></a>
                  <a href="#" className="w-9 h-9 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center text-white/60 hover:bg-white/20 hover:text-white transition-colors"><FiTwitter className="w-4 h-4" /></a>
                  <a href="#" className="w-9 h-9 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center text-white/60 hover:bg-white/20 hover:text-white transition-colors"><FiMail className="w-4 h-4" /></a>
                </div>
              </div>
            </div>

            {/* Founder Details */}
            <div className="lg:col-span-3 p-8 lg:p-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-50 text-accent-600 text-xs font-semibold mb-4">
                <FiAward className="w-3.5 h-3.5" /> Founder & Lead Advocate
              </div>
              <p className="text-secondary-600 leading-relaxed mb-6">{founder.bio}</p>
              
              {/* Specializations */}
              <h4 className="text-sm font-semibold text-secondary-900 mb-3 uppercase tracking-wider">Areas of Expertise</h4>
              <div className="flex flex-wrap gap-2 mb-8">
                {founder.specializations.map((spec, i) => (
                  <span key={i} className="px-3 py-1.5 bg-primary-50 text-primary-700 text-xs font-medium rounded-lg border border-primary-100">{spec}</span>
                ))}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {founder.stats.map((stat, i) => (
                  <div key={i} className="bg-secondary-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-primary-500">{stat.value}</p>
                    <p className="text-xs text-secondary-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section Heading */}
        <div className="text-center mb-10">
          <p className="text-sm font-semibold uppercase tracking-wider mb-2" style={{ color: '#d4af37' }}>Our Legal Team</p>
          <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-3">Experienced Advocates</h2>
          <p className="text-secondary-500 max-w-lg mx-auto">Each member of our team brings specialized expertise and a passion for justice</p>
        </div>

        {/* Team Grid */}
        <motion.div variants={{ visible: { transition: { staggerChildren: 0.1 } } }} initial="hidden" animate="visible" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {teamMembers.map(member => (
            <motion.div key={member.id} variants={fadeUp} className="group bg-white rounded-2xl border border-secondary-100 shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden">
              {/* Card Header */}
              <div className="bg-gradient-to-br from-primary-50 to-primary-100/50 p-6 text-center">
                <div className="w-20 h-20 rounded-2xl overflow-hidden mx-auto mb-3 group-hover:scale-105 transition-transform shadow-md ring-2 ring-primary-200">
                  <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="font-semibold text-secondary-900">{member.name}</h3>
                <p className="text-sm font-medium text-primary-500 mt-0.5">{member.role}</p>
                <p className="text-xs text-secondary-400 mt-1">{member.experience}</p>
              </div>

              {/* Card Body */}
              <div className="p-5">
                <p className="text-sm text-secondary-500 mb-4 leading-relaxed">{member.bio}</p>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {member.specializations.map((spec, i) => (
                    <span key={i} className="px-2 py-0.5 bg-primary-50 text-primary-600 text-xs rounded-md font-medium">{spec}</span>
                  ))}
                </div>
                <div className="flex items-center justify-center gap-3 pt-4 border-t border-secondary-100">
                  <a href="#" className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center text-secondary-400 hover:bg-primary-50 hover:text-primary-500 transition-colors"><FiLinkedin className="w-4 h-4" /></a>
                  <a href="#" className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center text-secondary-400 hover:bg-primary-50 hover:text-primary-500 transition-colors"><FiMail className="w-4 h-4" /></a>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Why Our Team Section */}
      <div className="max-w-6xl mx-auto px-4 pb-14">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: <FiShield className="w-6 h-6" />, title: 'Verified Advocates', desc: 'All lawyers are Bar Council verified and licensed to practice' },
            { icon: <FiBookOpen className="w-6 h-6" />, title: 'Deep Expertise', desc: 'Specialized knowledge across all areas of Indian law' },
            { icon: <FiUsers className="w-6 h-6" />, title: 'Client First', desc: 'Personalized approach ensuring the best outcome for every client' },
            { icon: <FiAward className="w-6 h-6" />, title: 'Proven Results', desc: 'Track record of successful case outcomes across Bihar courts' },
          ].map((item, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-white rounded-xl p-6 border border-secondary-100 shadow-card text-center">
              <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center text-primary-500 mx-auto mb-3">{item.icon}</div>
              <h4 className="font-semibold text-secondary-900 mb-1.5">{item.title}</h4>
              <p className="text-sm text-secondary-500">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Join CTA */}
      <div className="max-w-4xl mx-auto px-4 pb-20 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl p-10">
          <h2 className="text-2xl font-bold text-white mb-3">Need Legal Assistance?</h2>
          <p className="text-white/60 mb-6 max-w-md mx-auto text-sm">Our experienced team is ready to help you with your legal matters. Book a consultation today.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/booking" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-primary-600 font-semibold text-sm hover:bg-white/90 transition-colors">
              Book Consultation <FiArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-white/30 text-white font-semibold text-sm hover:bg-white/10 transition-colors">
              Contact Us
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default Team;
